import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileInput, FileOutput, Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ConvertersList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [converters, setConverters] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.group === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [converterResult, storeResult] = await Promise.allSettled([
        api.get("/converters"),
        api.get("/stores"),
      ]);
      if (converterResult.status === "rejected") throw converterResult.reason;
      setConverters(converterResult.value.data || []);
      if (storeResult.status === "fulfilled") {
        setStores(storeResult.value.data || []);
      } else {
        setStores([]);
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const storesById = useMemo(
    () => new Map(stores.map((store) => [store.id, store])),
    [stores]
  );

  const remove = async (converter) => {
    const store = storesById.get(converter.store_id);
    const storeName = converter.store_name || store?.name || t("converters.unknownStore");
    if (!window.confirm(t("converters.deleteConfirm", { name: storeName }))) return;

    try {
      await api.delete(`/converters/${converter.id}`);
      toast.success(t("converters.deleted"));
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div data-testid="converters-list-page">
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("converters.breadcrumb")}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900">
            {t("converters.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            {t("converters.description")}
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/converters/new"
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2"
            data-testid="add-converter-btn"
          >
            <Plus size={16} /> {t("converters.addConverter")}
          </Link>
        )}
      </div>

      <div className="pg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
                <th className="px-5 py-3 font-semibold">{t("converters.columns.store")}</th>
                <th className="px-5 py-3 font-semibold">{t("converters.columns.machineId")}</th>
                <th className="px-5 py-3 font-semibold">{t("converters.columns.type")}</th>
                <th className="px-5 py-3 font-semibold">{t("converters.columns.status")}</th>
                <th className="px-5 py-3 font-semibold text-right">{t("converters.columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                    {t("common.loading")}
                  </td>
                </tr>
              ) : converters.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-slate-400">
                    <div>{t("converters.noConverters")}</div>
                    {isAdmin && (
                      <Link
                        to="/converters/new"
                        className="inline-block mt-2 text-blue-600 hover:text-cyan-600 font-semibold"
                      >
                        {t("converters.createFirst")}
                      </Link>
                    )}
                  </td>
                </tr>
              ) : (
                converters.map((converter) => {
                  const store = storesById.get(converter.store_id);
                  const storeName = converter.store_name || store?.name || t("converters.unknownStore");
                  const machineId = converter.machine_id || store?.machine_id || "—";
                  return (
                    <tr
                      key={converter.id}
                      className="border-t border-slate-100 hover:bg-slate-50/50"
                      data-testid={`converter-row-${converter.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-800">{storeName}</div>
                        {store?.uid && <div className="text-xs text-slate-400 mt-0.5">{store.uid}</div>}
                      </td>
                      <td className="px-5 py-4 mono text-slate-700">{machineId}</td>
                      <td className="px-5 py-4">
                        <span className="pill pill-info">
                          {converterTypeLabel(t, converter.converter_type || converter.file_type)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={converter.active === false ? "pill pill-danger" : "pill pill-success"}>
                          {converter.active === false ? t("common.inactive") : t("common.active")}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <Link
                            to={`/converters/${converter.id}/input-log`}
                            className="px-3 py-2 rounded-md text-slate-600 bg-slate-50 hover:bg-cyan-50 hover:text-cyan-700 transition font-semibold text-xs flex items-center gap-1.5 whitespace-nowrap"
                            title={t("converters.inputLog")}
                            aria-label={t("converters.inputLog")}
                            data-testid={`view-converter-input-log-${converter.id}`}
                          >
                            <FileInput size={15} /> {t("converters.inputLog")}
                          </Link>
                          <Link
                            to={`/converters/${converter.id}/output-log`}
                            className="px-3 py-2 rounded-md text-slate-600 bg-slate-50 hover:bg-cyan-50 hover:text-cyan-700 transition font-semibold text-xs flex items-center gap-1.5 whitespace-nowrap"
                            title={t("converters.outputLog")}
                            aria-label={t("converters.outputLog")}
                            data-testid={`view-converter-output-log-${converter.id}`}
                          >
                            <FileOutput size={15} /> {t("converters.outputLog")}
                          </Link>
                          {isAdmin && (
                            <>
                            <button
                              onClick={() => navigate(`/converters/${converter.id}/edit`)}
                              className="p-2 rounded-md text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition"
                              title={t("common.edit")}
                              aria-label={t("common.edit")}
                              data-testid={`edit-converter-${converter.id}`}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => remove(converter)}
                              className="p-2 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition"
                              title={t("common.delete")}
                              aria-label={t("common.delete")}
                              data-testid={`delete-converter-${converter.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function converterTypeLabel(t, type) {
  if (type === "dayend_nextday") return t("converters.types.nextday");
  return t("converters.types.sameday");
}
